import { Fragment, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { Play, RotateCw, SendHorizonal, Square } from "lucide-react";

import { KoryHalo, koryStatusText, type KoryHaloState, type KoryLevelSource } from "@/components/kory/kory-halo";
import { MeetingRailSlot, MeetingUnit, useMeetingMode } from "@/components/kory/meeting";
import { PromptChip } from "@/components/kory/prompt-chip";
import { CaptionFold, CosLiveTurn } from "@/components/overview/cos-morning/cos-live-turn";
import type { LiveTurn, TrayLiveVoice } from "@/components/overview/cos-morning/cos-live-voice";
import { CosTalkControl, PRESS, TalkFinish } from "@/components/overview/cos-morning/cos-voice-mode";
import { Button } from "@/components/ui/button";
import { saidToday, useNextAsks } from "@/components/todos/tray/next-asks";
import type { DockedTrayBar } from "@/components/ui/docked-tray";
import { keepComposerFocus } from "@/lib/keep-composer-focus";
import { cn } from "@/lib/utils";

import { LastTurnLines } from "./last-turn";
import { lastTurn, openerTurn, replyAfter, type TypedTurnView } from "./typed-turn";
import type { CosSessionMessage } from "./types";

/** The turn the talk unit shows over its composer; `markdown` is null when it has no reply to link from. */
export type ComposerTurn = {
  turn: LiveTurn;
  markdown: string | null;
};

/** Talk on the side pane's folded rail (or, `bar`, a phone's collapsed bar at 44px); in Meeting
 * mode, Listen and the meeting's timer instead. */
export function KoryRailTalk({ voice, onTalk, bar = false }: { voice: TrayLiveVoice; onTalk: () => void; bar?: boolean }) {
  return <MeetingRailSlot bar={bar} talk={<RailTalk voice={voice} onTalk={onTalk} bar={bar} />} />;
}

/** Talk; while live, End (on the rail, a square stop); after a call that failed to start, Retry. Null without live voice. */
function RailTalk({ voice, onTalk, bar }: { voice: TrayLiveVoice; onTalk: () => void; bar: boolean }) {
  if (!voice.available) return null;
  if (voice.phase === "error") {
    return (
      <Button
        type="button"
        variant="outline"
        className={cn("gap-1 rounded-full border-primary/40 bg-card font-semibold", bar ? "h-11 px-4 text-sm" : "h-8 px-2.5 text-xs", PRESS)}
        onClick={() => void voice.start()}
      >
        <RotateCw className="size-3.5" aria-hidden />
        {voice.resume ?? "Retry"}
      </Button>
    );
  }
  const endTone = "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive";
  return voice.on ? (
    bar ? (
      <Button type="button" variant="outline" className={cn("h-11 gap-1 px-3 text-sm", endTone)} aria-label="End live voice" onClick={onTalk}>
        <Square className="size-3" aria-hidden />
        End
      </Button>
    ) : (
      // The rail is 56px: a square stop under the halo, sized to it.
      <Button
        type="button"
        variant="outline"
        className={cn("size-11 p-0", endTone)}
        aria-label="End call"
        title="End call"
        onClick={onTalk}
      >
        <Square className="size-4 fill-current" aria-hidden />
      </Button>
    )
  ) : (
    <Button
      type="button"
      variant="outline"
      className={cn("rounded-full border-primary/40 bg-card font-semibold", bar ? "h-11 px-4 text-sm" : "h-8 px-2.5 text-xs", PRESS)}
      aria-label="Talk with Kory"
      onClick={onTalk}
    >
      Talk
    </Button>
  );
}

/**
 * A folded tray's halo (the side pane's rail, a phone's collapsed bar): live, it follows the call's
 * phase and level exactly as the open tray's does; otherwise the host's resting state.
 */
export function KoryTrayHalo({
  voice,
  rest,
  size,
}: {
  voice: TrayLiveVoice;
  rest: { state: KoryHaloState; level: KoryLevelSource | null };
  size: number;
}) {
  const live = voice.available && voice.on;
  return <KoryHalo size={size} state={live ? voice.halo : rest.state} level={live ? voice.level : rest.level} />;
}

/**
 * A live turn on one line, newest words in view: Kory's reply once it starts, else your words, else
 * where the call is. It never grows; older words slide out at the left, under a fade.
 */
export function KoryLiveTicker({ turn, error }: { turn: LiveTurn; error: string | null }) {
  const said =
    turn.kory.tone !== "hint" && turn.kory.text
      ? { who: "Kory", text: turn.kory.text }
      : turn.heard?.text
        ? { who: "You", text: turn.heard.text }
        : null;
  const text = error ?? said?.text ?? (turn.kory.text || turn.status);
  const boxRef = useRef<HTMLSpanElement>(null);
  const wordsRef = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const words = wordsRef.current;
    if (box && words) setClipped(words.scrollWidth > box.clientWidth + 1);
  }, [text]);
  return (
    <span data-testid="kory-bar-ticker" className="flex min-w-0 flex-1 items-center gap-1.5">
      <span aria-hidden className="size-2 shrink-0 rounded-full bg-destructive motion-safe:animate-pulse" />
      {said && !error ? (
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-foreground">{said.who}</span>
      ) : null}
      {/* Pinned to the end, so an overflowing line clips its start, not its newest words. */}
      <span
        ref={boxRef}
        aria-live="polite"
        className={cn(
          "flex min-w-0 flex-1 justify-end overflow-hidden",
          clipped && "[mask-image:linear-gradient(to_right,transparent,black_1.5em)]",
        )}
      >
        <span
          ref={wordsRef}
          className={cn(
            "min-w-full shrink-0 whitespace-nowrap",
            error ? "text-destructive" : said ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {text}
        </span>
      </span>
    </span>
  );
}

/**
 * A phone tray's collapsed bar, the same for both trays: the one halo (live, it ends your turn on a
 * tap), one status line (the live caption while talking, else the host's `idle` line), and Talk or End.
 */
export function koryTrayBar({
  voice,
  rest,
  idle,
  onTalk,
}: {
  voice: TrayLiveVoice;
  rest: { state: KoryHaloState; level: KoryLevelSource | null };
  idle: ReactNode;
  onTalk: () => void;
}): DockedTrayBar {
  const live = voice.available && voice.on;
  return {
    mark: (
      <span data-testid="kory-bar-halo" className="relative grid size-11 shrink-0 place-items-center">
        <KoryTrayHalo voice={voice} rest={rest} size={24} />
        {live ? <TalkFinish finish={voice.finish} /> : null}
      </span>
    ),
    status: live ? <KoryLiveTicker turn={voice.turn} error={voice.error} /> : idle,
    actions: <KoryRailTalk voice={voice} onTalk={onTalk} bar />,
  };
}

/**
 * Last turn only: what you just typed, else the session's latest exchange from anywhere; the rest
 * of today is the session on /kory. Clicking into the box after a call opens on what voice last said,
 * until the session moves on past it (a newer exchange from anywhere).
 */
export function useComposerTurn({
  sessionOn,
  messages,
  typed,
  replying,
  voice,
  opened,
}: {
  sessionOn: boolean;
  messages: CosSessionMessage[];
  typed: TypedTurnView | null;
  replying: boolean;
  voice: LiveTurn;
  /** The composer has been clicked into this visit. */
  opened: boolean;
}): ComposerTurn | null {
  // The session's length when live voice last spoke: its line leads only until the session moves past it.
  const voiceMarkRef = useRef({ text: "", at: 0 });
  const voiceText = voice.kory.tone !== "hint" ? voice.kory.text : "";
  if (voiceText !== voiceMarkRef.current.text) voiceMarkRef.current = { text: voiceText, at: messages.length };
  const voiceFresh = !sessionOn || messages.length <= voiceMarkRef.current.at;
  const voiceOpener = opened && voiceFresh ? openerTurn(voice, []) : null;
  const sessionTurn = !sessionOn
    ? null
    : typed
      ? { turn: typed.turn, at: typed.at, markdown: replyAfter(messages, typed.at) }
      : voiceOpener
        ? null
        : lastTurn(messages, { replying });
  const turn = sessionTurn?.turn ?? voiceOpener;
  if (!turn) return null;
  return { turn, markdown: sessionTurn?.markdown ?? null };
}

/** What the tray offers under its composer: starter prompts before your first turn, then each reply's next asks.
 *  A numbers page or an attached cell offers its own openers in their place. */
export type TrayAsks = { kind: "starters" | "next" | "openers"; asks: readonly string[] };

/**
 * Before you say anything to Kory today, the starters; after, the 2-3 next asks Kory's latest reply
 * offered, or none when it offered none. Null without the session (the tray has no turns to follow).
 */
export function useTrayAsks({
  session,
  messages,
  typed,
  voice,
  starters,
}: {
  /** The session when it is on (its conversation and today's date), else null. */
  session: { conversationId: string | null; day: string } | null;
  messages: CosSessionMessage[];
  typed: TypedTurnView | null;
  voice: LiveTurn;
  starters: readonly string[];
}): TrayAsks | null {
  const asks = useNextAsks(session?.conversationId ?? null);
  if (!session) return null;
  if (asks.length) return { kind: "next", asks };
  const said = Boolean(typed) || Boolean(voice.heard?.text) || saidToday(messages, session.day);
  return said || !starters.length ? null : { kind: "starters", asks: starters };
}

/** Up to three half-width suggestion pills: two share a row, a third wraps to a second. `hidden`: lookup rows cover them. */
export function AskChips({
  trayAsks,
  onPick,
  hidden = false,
}: {
  trayAsks: TrayAsks;
  onPick: (ask: string) => void;
  hidden?: boolean;
}) {
  const label = trayAsks.kind === "starters" ? "Starter prompts" : trayAsks.kind === "openers" ? "Page prompts" : "Next asks";
  const shown = trayAsks.asks.slice(0, 3);
  const [touching, setTouching] = useState(false);
  return (
    <div
      data-testid="cos-tray-asks"
      data-kind={trayAsks.kind}
      className="h-full px-1"
      onPointerDown={(event) => event.pointerType === "touch" && setTouching(true)}
      onPointerUp={() => setTouching(false)}
      onPointerCancel={() => setTouching(false)}
    >
      <ul aria-label={label} className="flex flex-wrap content-start gap-1.5">
        {shown.map((ask, index) => (
          <li key={ask} className="min-w-0 w-[calc(50%-0.375rem)]">
            <PromptChip
              onClick={() => onPick(ask)}
              className="h-11 w-full sm:h-8"
              title={ask}
              scrollOnTouch
              scrollOnFine
              scrollDelay={index * 160}
              pauseScroll={touching}
              hidden={hidden}
            >
              {ask}
            </PromptChip>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The one Kory talk unit, in Overview's tray and the global tray alike, so the two never drift.
 * Typing: the last turn as a "You:" and a "Kory:" line, over one row: the Kory halo with Talk beside
 * it, the composer, then Send. Live voice: the same row in place, the halo where it was, the composer
 * giving way to the time live and End, with the live captions over it. `readAloud` is Overview's voice mode for people without live voice: its switch takes
 * Talk's place and, while a beat waits on you, its bar takes the composer's.
 */
export function KoryTalkUnit({
  voice,
  mobile,
  onTalk,
  rest,
  turn,
  composer,
  send,
  beforeSend,
  status,
  liveLine,
  readAloud,
  banner,
  footer,
  trayAsks,
  compact = false,
}: {
  voice: TrayLiveVoice;
  mobile: boolean;
  /** Starts live voice, or ends it (also "Type instead" after an error). */
  onTalk: () => void;
  /** The one halo at rest: leading the row beside Talk, or beside the composer without live voice. */
  rest: { state: KoryHaloState; level: KoryLevelSource | null };
  turn: ComposerTurn | null;
  composer: {
    ref?: Ref<HTMLTextAreaElement>;
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    onFocus?: () => void;
    /** Runs before Enter sends; a handled key (preventDefault) stops there. */
    onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder: string;
    label: string;
    /** Nothing can be sent from here right now (a paused Drive in focus). */
    disabled?: boolean;
  };
  /** `onStop`: Kory is working on a turn, so Send's spot is Stop, which cancels it. */
  send: { ref?: Ref<HTMLButtonElement>; disabled: boolean; onStop?: () => void };
  /** Controls left of Send (Overview's stop for read-aloud and a hands-free take). */
  beforeSend?: ReactNode;
  /** A line over the composer row (Overview's hands-free take status). */
  status?: ReactNode;
  /** What Kory is doing, said to screen readers; the halo shows it. */
  liveLine: { state: KoryHaloState; verb?: string | null };
  readAloud?: { toggle: ReactNode; bar: ReactNode | null; controls: ReactNode };
  banner?: ReactNode;
  footer?: ReactNode;
  /** Starters, then each reply's next asks: typing only, hidden while you type, never read aloud. */
  trayAsks?: { value: TrayAsks | null; onPick: (ask: string) => void };
  /** The person is working in the tray's to-dos: the turn on one line, the asks folded away, the row kept. */
  compact?: boolean;
}) {
  useEffect(() => {
    const prefetch = () => {
      void import("@/lib/voice-live/speech-detector").then(({ prefetchSpeechDetector }) => prefetchSpeechDetector()).catch(() => undefined);
    };
    const idle = window.requestIdleCallback?.(prefetch, { timeout: 1500 });
    const fallback = idle === undefined ? window.setTimeout(prefetch, 250) : undefined;
    return () => {
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (fallback !== undefined) window.clearTimeout(fallback);
    };
  }, []);
  const meeting = useMeetingMode();
  const liveBar = voice.available && voice.on;
  const readAloudBar = liveBar ? null : (readAloud?.bar ?? null);
  // The composer fades back in only after a call, never on first paint.
  const [prevLive, setPrevLive] = useState(liveBar);
  const [afterCall, setAfterCall] = useState(false);
  if (liveBar !== prevLive) {
    setPrevLive(liveBar);
    if (!liveBar) setAfterCall(true);
  }
  const voiceMode = liveBar || Boolean(readAloudBar);
  const liveStatus = koryStatusText(liveLine.state, liveLine.verb);
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    composer.onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      composer.onSend();
    }
  };
  const talkControl = (
    <CosTalkControl
      live={liveBar}
      state={liveBar ? voice.halo : rest.state}
      level={liveBar ? voice.level : rest.level}
      mobile={mobile}
      onTalk={onTalk}
      onEnd={onTalk}
      finish={voice.finish}
      starting={voice.phase === "connecting"}
      failed={voice.phase === "error"}
    />
  );
  // Meeting mode: the one voice button is Listen, and the unit is the meeting's.
  if (meeting.mode === "meeting") return <MeetingUnit />;
  return (
    <div
      data-testid="cos-talk-unit"
      data-mode={voiceMode ? "voice" : "typing"}
      className={cn(
        "group/talk rounded-2xl border bg-background/85 p-1.5 shadow-sm transition-[border-color,box-shadow] duration-150 sm:p-2",
        "focus-within:border-primary/50 focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--primary)_14%,transparent)]",
      )}
    >
      <style>{`
        .cos-composer-input { caret-color: var(--primary) }
        .dark .cos-composer-input { caret-color: var(--warning) }
        /* A terminal-style block caret where the browser has one; elsewhere the brand-colored bar. */
        @supports (caret-shape: block) {
          .cos-composer-input { caret-shape: block }
        }
      `}</style>
      {banner}
      {liveBar ? (
        <div data-testid="cos-voice-bar" data-phase={voice.phase} className="relative flex items-center gap-4 px-1 py-1">
          {/* Live voice's halo is in the Talk control below. */}
          <div className="min-w-0 flex-1">
            {voice.error ? (
              <p role="alert" className="line-clamp-3 text-base leading-snug text-destructive">
                {voice.error}
              </p>
            ) : (
              <CosLiveTurn turn={voice.turn} mobile={mobile} listenLinks={voice.listenLinks} compact={compact} />
            )}
            {voice.needsAudioTap ? (
              <Button type="button" variant="outline" className="mt-2 h-11 gap-1.5 px-3" onClick={voice.hearKory}>
                <Play className="size-4" aria-hidden />
                Tap to hear Kory
              </Button>
            ) : null}
            {voice.error ? (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {/* Retry starts the call again; Type instead closes it. */}
                <Button type="button" variant="outline" className={cn("h-11 gap-1.5 px-3", PRESS)} onClick={() => void voice.start()}>
                  <RotateCw className="size-4" aria-hidden />
                  {voice.resume ?? "Retry"}
                </Button>
                <button
                  type="button"
                  onClick={onTalk}
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
                >
                  Type instead
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : readAloudBar ? (
        readAloudBar
      ) : (
        // Room for the Talk halo's overhang above the row, so it never sits on the reply's last line.
        <div className={cn(voice.available && (turn || status) && (mobile ? "pb-2" : "pb-3"))}>
          {turn ? (
            <div data-testid="cos-typed-turn" className="mb-1">
              <LastTurnLines turn={turn.turn} markdown={turn.markdown} oneLine={compact} />
            </div>
          ) : null}
          {status ? <div className="mb-1 flex min-h-6 items-center px-1">{status}</div> : null}
          {trayAsks?.value?.asks.length && !composer.value.trim() ? (
            // Typing on a phone, the keyboard takes half the screen: the asks fold away so the box stays in view.
            <CaptionFold open={!compact} className={cn(mobile && "group-has-[textarea:focus]/talk:hidden")}>
              <AskChips trayAsks={trayAsks.value} onPick={trayAsks.onPick} />
            </CaptionFold>
          ) : null}
          <span className="sr-only" aria-live="polite">
            {liveStatus ? `Kory: ${liveStatus}` : ""}
          </span>
        </div>
      )}
      {/* One row. Typing: the halo with Talk beside it (the halo beside the box without live voice),
          the box, anything before Send, then Send; focus shows on the frame. Live: the same halo in
          place, the time live and End. Read-aloud: its switch and controls. The controls are keyed, so
          Talk and the switch stay the same element (and keep focus) as it swaps. */}
      <div
        data-testid="cos-talk-toolbar"
        className={cn(
          "flex gap-1.5",
          liveBar ? "mt-4 items-center" : voiceMode ? "mt-2 items-center gap-2 border-t pt-2" : voice.available ? "items-center" : "items-end",
        )}
      >
        {voiceMode ? null : voice.available ? null : (
          <div key="halo" className="grid size-11 shrink-0 place-items-center">
            <KoryHalo size={26} state={rest.state} level={rest.level} />
          </div>
        )}
        {voice.available ? <Fragment key="talk">{talkControl}</Fragment> : null}
        {voiceMode ? null : (
          <div
            key="composer"
            data-testid="cos-composer"
            className={cn(
              "flex min-w-0 flex-1",
              afterCall && "animate-in fade-in duration-200 ease-out motion-reduce:animate-none",
            )}
          >
            <textarea
              ref={composer.ref}
              value={composer.value}
              onFocus={composer.onFocus}
              onChange={(event) => composer.onChange(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={composer.placeholder}
              aria-label={composer.label}
              disabled={composer.disabled}
              rows={1}
              className="cos-composer-input block max-h-32 min-h-11 min-w-0 flex-1 resize-none bg-transparent px-2 py-2.5 text-base leading-6 outline-none [field-sizing:content] placeholder:text-muted-foreground focus-visible:outline-none md:text-sm"
            />
          </div>
        )}
        {voice.available ? null : <Fragment key="talk">{readAloud?.toggle}</Fragment>}
        {voiceMode ? (
          liveBar ? null : (
            <Fragment key="controls">{readAloud?.controls}</Fragment>
          )
        ) : (
          <Fragment key="send">
            {beforeSend}
            {/* The press keeps focus in the box, so a phone's keyboard stays up and the tap lands. */}
            {send.onStop ? (
              <Button
                type="button"
                className="size-11 shrink-0 p-0"
                ref={send.ref}
                aria-label="Stop Kory"
                title="Stop"
                onMouseDown={keepComposerFocus}
                onClick={send.onStop}
              >
                <Square className="size-3.5 fill-current" aria-hidden />
              </Button>
            ) : (
              <Button
                type="button"
                className="size-11 shrink-0 p-0"
                ref={send.ref}
                aria-label="Send"
                disabled={send.disabled}
                data-kory-send
                onMouseDown={keepComposerFocus}
                onClick={composer.onSend}
              >
                <SendHorizonal className="size-4" aria-hidden />
              </Button>
            )}
          </Fragment>
        )}
      </div>
      {footer}
    </div>
  );
}
